import { useMemo, useState } from "react";

import { isWithinWorkRequirementAgeRange } from "@libs/work-requirements";
import { CoverageEventsFlowStepWrapper } from "@/blocks/CoverageEventsFlowStepWrapper";
import { WorkRequirementSelectableRow } from "@/blocks/WorkRequirementSelectableRow";
import { WorkRequirementsTitle } from "@/blocks/WorkRequirementsTitle";
import { VnBox } from "@/components/VnBox";
import { VnLoadingSpinner } from "@/components/VnLoadingSpinner";
import { VnSeparator } from "@/components/VnSeparator";
import { VnText } from "@/components/VnText/VnText";
import { useListHouseholdMembersQuery } from "@/data/queries/householdMembersQueries";
import { toScreeningMembers } from "@/domain/workRequirements";
import { useWorkRequirementScreeningStep } from "@/hooks/useWorkRequirementScreeningStep";
import { dateAsDayjs } from "@/utils/date";
import { formatHouseholdMemberName } from "@/utils/householdMember";

export const WorkRequirementWhoScreen = () => {
  const { workRequirement, isLoading, isSaving, saveAndContinue, goBack } =
    useWorkRequirementScreeningStep();
  const { data: householdMembers, isPending: isLoadingHouseholdMembers } =
    useListHouseholdMembersQuery();

  // Null until the patient changes the selection, so the saved answers show
  // first and a background refetch never overwrites their edits.
  const [editedMemberIds, setEditedMemberIds] = useState<string[] | null>(null);

  const eligibleMembers = useMemo(() => {
    if (!workRequirement) return [];
    const today = dateAsDayjs().format("YYYY-MM-DD");

    return (householdMembers ?? []).filter(member =>
      isWithinWorkRequirementAgeRange({
        rule: workRequirement.rule,
        dateOfBirth: member.dateOfBirth,
        today,
      }),
    );
  }, [householdMembers, workRequirement]);

  if (isLoading || isLoadingHouseholdMembers || !workRequirement) {
    return (
      <CoverageEventsFlowStepWrapper
        onBack={goBack}
        shouldShowNextButton={false}>
        <VnLoadingSpinner />
      </CoverageEventsFlowStepWrapper>
    );
  }

  const { rule } = workRequirement;
  const eligibleMemberIds = new Set(eligibleMembers.map(({ id }) => id));
  const selectedMemberIds =
    editedMemberIds ??
    workRequirement.members
      .map(({ householdMemberId }) => householdMemberId)
      .filter(householdMemberId => eligibleMemberIds.has(householdMemberId));
  const isEveryoneSelected =
    eligibleMembers.length > 0 &&
    eligibleMembers.every(member => selectedMemberIds.includes(member.id));

  const toggleMember = (memberId: string) =>
    setEditedMemberIds(
      selectedMemberIds.includes(memberId)
        ? selectedMemberIds.filter(id => id !== memberId)
        : [...selectedMemberIds, memberId],
    );

  const toggleEveryone = () =>
    setEditedMemberIds(
      isEveryoneSelected ? [] : eligibleMembers.map(({ id }) => id),
    );

  const handleNext = () => {
    // Keep earlier answers for members who stay selected.
    const savedMembers = toScreeningMembers(workRequirement);

    saveAndContinue({
      lastStep: "WHO",
      members: selectedMemberIds.map(
        householdMemberId =>
          savedMembers.find(
            member => member.householdMemberId === householdMemberId,
          ) ?? { householdMemberId, exemptions: [] },
      ),
    });
  };

  return (
    <CoverageEventsFlowStepWrapper
      onBack={goBack}
      onNext={handleNext}
      isNextButtonLoading={isSaving}
      isNextButtonDisabled={selectedMemberIds.length === 0}>
      <WorkRequirementsTitle i18nKey="native:workRequirements.who.title" />
      <VnText
        variant="lg"
        className="text-muted-foreground mt-2"
        i18nKey="native:workRequirements.whatAreDescription"
        i18nOptions={{
          hours: rule.requiredMonthlyHours,
          minAge: rule.minAge,
          maxAge: rule.maxAge,
        }}
      />

      {eligibleMembers.length === 0 ? (
        <VnText
          variant="lg"
          className="mt-4"
          i18nKey="native:workRequirements.who.noEligibleMembers"
          i18nOptions={{ minAge: rule.minAge, maxAge: rule.maxAge }}
        />
      ) : (
        <>
          <VnText
            variant="lg"
            className="text-muted-foreground mt-1"
            i18nKey="native:workRequirements.who.question"
          />
          <VnBox className="mt-4 gap-3">
            {eligibleMembers.length > 1 && (
              <WorkRequirementSelectableRow
                isChecked={isEveryoneSelected}
                onToggle={toggleEveryone}
                i18nKey="native:workRequirements.who.checkAll"
              />
            )}
            <VnSeparator className="my-2" />
            <VnBox className="gap-5">
              {eligibleMembers.map(member => (
                <WorkRequirementSelectableRow
                  key={member.id}
                  isChecked={selectedMemberIds.includes(member.id)}
                  onToggle={() => toggleMember(member.id)}>
                  {formatHouseholdMemberName(member)}
                </WorkRequirementSelectableRow>
              ))}
            </VnBox>
          </VnBox>
        </>
      )}
    </CoverageEventsFlowStepWrapper>
  );
};
